/**
 * Admin Axios HTTP 客户端实例 + 拦截器
 *
 * 统一处理 Admin JWT 注入、响应格式解析、401 跳转登录。
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { API_CONFIG, STORAGE_KEYS } from '@chaoyinli/shared'
import type { ApiResponse } from '@chaoyinli/shared'

// 创建 axios 实例
const instance: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ===== 请求拦截器 =====
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 注入 Admin JWT
    const jwt = localStorage.getItem(STORAGE_KEYS.ADMIN_JWT)
    if (jwt) {
      // 去除可能的引号（JSON 序列化导致的）
      const cleanJwt = jwt.replace(/^"|"$/g, '')
      config.headers.Authorization = `Bearer ${cleanJwt}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ===== 响应拦截器 =====
instance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const res = response.data

    // code === 0 表示成功，返回 data
    if (res.code === 0) {
      return res.data as any
    }

    // code !== 0 表示业务错误
    const errorMsg = res.message || '请求失败，请稍后重试'
    return Promise.reject(new Error(errorMsg))
  },
  (error) => {
    // HTTP 401 → 清除 JWT 并跳转登录页
    if (error.response?.status === 401) {
      localStorage.removeItem(STORAGE_KEYS.ADMIN_JWT)
      localStorage.removeItem('cy_admin_username')
      localStorage.removeItem('cy_admin_role')
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
      return Promise.reject(new Error('登录已过期，请重新登录'))
    }

    // 网络错误
    if (error.message === 'Network Error') {
      return Promise.reject(new Error('网络连接失败，请检查网络设置'))
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error('请求超时，请稍后重试'))
    }

    // 其他 HTTP 错误
    const status = error.response?.status
    const msg = error.response?.data?.message || `请求错误（${status || '未知'}）`
    return Promise.reject(new Error(msg))
  },
)

// ===== 封装便捷函数 =====

/**
 * GET 请求
 */
export async function apiGet<T = any>(url: string, params?: Record<string, any>): Promise<T> {
  return instance.get(url, { params }) as unknown as Promise<T>
}

/**
 * POST 请求
 */
export async function apiPost<T = any>(url: string, data?: Record<string, any>): Promise<T> {
  return instance.post(url, data) as unknown as Promise<T>
}

/**
 * PUT 请求
 */
export async function apiPut<T = any>(url: string, data?: Record<string, any>): Promise<T> {
  return instance.put(url, data) as unknown as Promise<T>
}

/**
 * PATCH 请求（部分更新，只传需要变更的字段）
 */
export async function apiPatch<T = any>(url: string, data?: Record<string, any>): Promise<T> {
  return instance.patch(url, data) as unknown as Promise<T>
}

/**
 * DELETE 请求
 */
export async function apiDelete<T = any>(url: string, params?: Record<string, any>): Promise<T> {
  return instance.delete(url, { params }) as unknown as Promise<T>
}

export default instance
